import {
  Component,
  signal,
  computed,
  inject,
  ElementRef,
  OnInit,
  input,
  effect,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ProfessionalsDataService } from '../../../services/professionals-data.service';
import { ProfessionalsWizardService } from '../../../services/professionals-wizard.service';
import { UserSessionService } from '../../../../core/services/mock-auth.service';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { FormDropdownService } from '../../../../core/services/form-dropdown.service';
import { GeographyService } from '../../../../core/services/geography.service';
import { ComponentCanDeactivate } from '../../../../core/guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { convertEmptyStringsToNull } from '../../../utils/form-data.utils';
import {
  formatIban,
  hasInvalidIbanCharacters,
  isItalianIban,
  isValidIbanFormat,
  normalizeIban,
} from '../../../../core/validators/iban.utils';
import {
  hasMeaningfulText,
  isMissingMeaningfulText,
  normalizeTrimmedText,
} from '../../../../core/validators/text-value.utils';
import { catchError, of, forkJoin, Observable } from 'rxjs';
import { RegistrationStepService, RegistrationStepResponse, RegistrationStatus, RegistrationStep } from '../../../../core/services/registration-step.service';
import { ValidationErrorSummaryComponent } from '../../validation-error-summary/validation-error-summary.component';
import { ValidationErrorSection, validationSectionsFromMessages } from '../../validation-error-summary/validation-error-summary.model';
import { PhoneInputComponent } from '../../../../core/shared/phone-input/phone-input.component';
import {
  DEFAULT_PHONE_PREFIX,
  isPhoneNumberDigitsOnly,
  isValidFullPhoneNumber,
  isValidNationalPhoneNumber,
  splitPhoneNumber,
} from '../../../../core/shared/phone-input/phone-prefixes';
import { sortTaxRegimeOptions } from '../../../../core/utils/tax-regime-order.utils';

interface DropdownOption {
  label: string;
  value: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-professionals-general-data',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    AccordionModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    DatePickerModule,
    RadioButtonModule,
    ToastModule,
    TooltipModule,
    ValidationErrorSummaryComponent,
    PhoneInputComponent,
  ],
  templateUrl: './professionals-general-data.component.html',
  styleUrl: './professionals-general-data.component.scss',
})
export class ProfessionalsGeneralDataComponent implements OnInit, ComponentCanDeactivate {
  readonly hasMeaningfulText = hasMeaningfulText;
  readonly isMissingMeaningfulText = isMissingMeaningfulText;
  readonly today = new Date(); // For date picker maxDate

  // Optional input for professional ID (used in review mode)
  professionalIdInput = input<string | null>(null);
  
  // Optional input to force readonly mode (for review/detail pages)
  readonly = input<boolean>(false);

  private router = inject(Router);
  private professionalsDataService = inject(ProfessionalsDataService);
  protected professionalsWizardService = inject(ProfessionalsWizardService);
  private mockAuthService = inject(UserSessionService);
  private professionalsService = inject(ProfessionalsService);
  private formDropdownService = inject(FormDropdownService);
  private geographyService = inject(GeographyService);
  private messageService = inject(MessageService);
  private registrationStepService = inject(RegistrationStepService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  // SessionStorage key for draft data
  private readonly DRAFT_STORAGE_KEY = 'draft_general_data';
  private readonly UNSAVED_CHANGES_MESSAGE = 'Se cambi sezione o lasci la pagina senza cliccare su "Salva bozza", perderai i dati inseriti. Vuoi procedere?';
  private allowNextNavigation = false;

  // Live region message for screen readers
  liveRegionMessage = signal<string>('');
  validationErrorSections = signal<ValidationErrorSection[]>([]);

  // Current registration step (for detecting modification requests)
  currentRegistrationStep = signal<RegistrationStepResponse | null>(null);
  
  // Step history (to check for MODIFICATION_REQUESTED)
  stepHistory = signal<RegistrationStepResponse[]>([]);

  // Computed: Check if there's a modification request
  hasModificationRequest = computed(() => {
    const step = this.currentRegistrationStep();
    const history = this.stepHistory();
    
    if (!step || this.professionalIdInput()) return false; // Don't show in review mode
    
    // Only show banner if:
    // 1. Current step is DATA_INPUT with USER_INPUT status
    // 2. There's a VERIFICATION step with MODIFICATION_REQUESTED in history
    if (step.step === 'DATA_INPUT' && step.status === RegistrationStatus.USER_INPUT) {
      return history.some(s => 
        s.step === 'VERIFICATION' && 
        s.status === RegistrationStatus.MODIFICATION_REQUESTED
      );
    }
    
    return false;
  });
  
  // Computed: Check if all fields should be disabled (readonly mode or review mode)
  isFieldsDisabled = computed(() => {
    return this.readonly() || this.professionalIdInput() !== null;
  });

  // Computed: Get modification comment from VERIFICATION MODIFICATION_REQUESTED step
  modificationComment = computed(() => {
    const history = this.stepHistory();
    
    // Find the most recent VERIFICATION with MODIFICATION_REQUESTED
    const modificationSteps = history
      .filter(s => s.step === 'VERIFICATION' && s.status === RegistrationStatus.MODIFICATION_REQUESTED)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    if (modificationSteps.length > 0) {
      // Parse "Modification requested: actual message" format
      const comment = modificationSteps[0].comments || '';
      return comment.replace(/^Modification requested:\s*/, '').trim();
    }
    
    return null;
  });

  // Signal for expand/collapse state of modification notes
  isModificationNotesExpanded = signal<boolean>(false);
  private readonly MAX_COMMENT_LENGTH = 150;

  // Computed: Check if comment is long
  isCommentLong = computed(() => {
    const comment = this.modificationComment();
    return comment ? comment.length > this.MAX_COMMENT_LENGTH : false;
  });

  // Computed: Get truncated comment
  displayedComment = computed(() => {
    const comment = this.modificationComment();
    if (!comment) return null;
    
    if (this.isCommentLong() && !this.isModificationNotesExpanded()) {
      return comment.substring(0, this.MAX_COMMENT_LENGTH) + '...';
    }
    
    return comment;
  });

  // Toggle modification notes expansion
  toggleModificationNotes(): void {
    this.isModificationNotesExpanded.update(expanded => !expanded);
  }

  // Computed: verifica se lo step corrente è completato
  isStepCompleted = computed(() => {
    const currentStep = this.professionalsWizardService.steps().find(step => step.id === 0);
    return currentStep?.completed ?? false;
  });

  // Computed: verifica se siamo in modalità read-only
  isReadOnly = computed(() => {
    return this.professionalsDataService.isReadOnly() || this.readonly() || this.professionalIdInput() !== null;
  });

  professionalSummaryBusinessName = computed(() => {
    const businessName = normalizeTrimmedText(this.businessName());
    if (businessName) return businessName;

    return [this.lastname() || this.preRegLastName(), this.firstname() || this.preRegFirstName()]
      .map(value => normalizeTrimmedText(value))
      .filter(Boolean)
      .join(' ');
  });

  professionalSummaryType = computed(() => this.preRegTypeLabel() || this.preRegType());

  professionalIdDisplay = computed(() =>
    this.professionalIdInput() || this.mockAuthService.getDemoProfessionalId() || ''
  );

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.shouldWarnBeforeLeaving()) {
      event.preventDefault();
      event.returnValue = true;
    }
  }

  constructor() {
    console.log('🏗️ [General Data] Constructor - Component created');
    // Note: Tipologia will be initialized from API response in populateFormFromData()
    // Effect to ensure filtered types recalculate when dependencies change
  effect(() => {
    const personType = this.legalOrPhysical();
    const allTypes = this.allSupplierTypes();
    const filtered = this.filteredSupplierTypes();
    
    console.log('🔄 [Effect] Filtered types:', {
      personType,
      allTypesCount: allTypes.length,
      filteredCount: filtered.length
    });
  });
  }

  /**
   * Get current professional ID from input or mockAuthService
   */
  private getCurrentProfessionalId(): string | null {
    return this.professionalIdInput() || this.mockAuthService.getDemoProfessionalId();
  }

  // Pre-Registration readonly data (from pre-registration step)
  preRegType = signal<string>('');
  preRegBodyShopType = signal<string>('');
  preRegFirstName = signal<string>('');
  preRegLastName = signal<string>('');
  preRegEmail = signal<string>('');
  preRegTypeLabel = signal<string>('');
  preRegBodyShopTypeLabel = signal<string>('');

  // Company Data
  businessName = signal<string>('');
  vatNumber = signal<string>('');
  invoiceVatNumberSameAsVatNumber = signal<boolean>(true);
  legalOrPhysical = signal<string | null>(null); // PHYSICAL_PERSON or LEGAL_PERSON
  companyType = signal<string>('');
  companyTypeError = signal<string>('');
  fiscalCode = signal<string>('');
  fiscalCodeError = signal<string>('');

  // Structure Address (legalAddress)
  streetType = signal<string>('');
  streetName = signal<string>('');
  streetNumber = signal<number | null>(null);
  zipCode = signal<string>('');
  municipality = signal<string>('');
  province = signal<string>('');
  region = signal<string>('');
  invoiceAddressSameAsLegalAddress = signal<boolean>(true);

  // Contatti Istituzionali (contactInfo)
  institutionalEmailSameAsRegistrationEmail = signal<boolean>(true);
  email = signal<string>('');
  certifiedEmail = signal<string>('');
  mobilePhone = signal<string>('');
  mobilePhonePrefix = signal<string>(DEFAULT_PHONE_PREFIX);
  telephone = signal<string>('');
  telephonePrefix = signal<string>(DEFAULT_PHONE_PREFIX);
  website = signal<string>('');

  // Contatti Rappresentante Legale (contactInfo)
  responsibleEmailSameAsRegistrationEmail = signal<boolean>(true);
  responsibleEmail = signal<string>('');
  mobilePhoneResponsible = signal<string>('');
  mobilePhoneResponsiblePrefix = signal<string>(DEFAULT_PHONE_PREFIX);
  ownerTelephone = signal<string>('');
  ownerTelephonePrefix = signal<string>(DEFAULT_PHONE_PREFIX);
  claimsNetworkPhone = signal<string>('');
  claimsNetworkPhonePrefix = signal<string>(DEFAULT_PHONE_PREFIX);
  private shouldPrefillClaimsNetworkPhone = true;

  // Banking and Tax Details (bankDetails)
  invoiceParty = signal<string>('');
  private invoicePartyAutoFillValue: string | null = null;
  invoiceStreetType = signal<string>('');
  invoiceAddress = signal<string>('');
  invoiceStreetNumber = signal<number | null>(null);
  invoiceZipCode = signal<string>('');
  invoiceMunicipality = signal<string>('');
  invoiceProvince = signal<string>('');
  invoiceRegion = signal<string>('');
  invoiceVatNumber = signal<string>('');
  fiscalRegime = signal<string>('');
  iban = signal<string>('');
  ibanInvalidCharacterError = computed(() => hasInvalidIbanCharacters(this.iban()));
  ibanFormatError = computed(() => {
    const value = normalizeIban(this.iban());
    if (!value) {
      return false;
    }

    if (!isValidIbanFormat(value)) {
      return true;
    }

    return value.startsWith('IT') && !isItalianIban(value);
  });
  bankInstitutionName = signal<string>('');
  bankInstitutionBranch = signal<string>('');
  accountOwner = signal<string>('');
  accountOwnerMismatch = computed(() => {
    if (this.isOperationalLocationMode()) {
      return false;
    }

    const accountOwner = normalizeTrimmedText(this.accountOwner());
    if (!accountOwner) {
      return false;
    }

    const expectedAccountOwner = this.getExpectedAccountOwner();
    return !expectedAccountOwner ||
      this.normalizeAccountOwnerValue(accountOwner) !== this.normalizeAccountOwnerValue(expectedAccountOwner);
  });
  swift = signal<string>('');
  swiftFormatError = computed(() => this.isInvalidSwiftFormat(this.swift()));

  // Legal Representative Data (legalRepresentative)
  firstname = signal<string>('');
  lastname = signal<string>('');
  birthMunicipality = signal<string>('');
  dateOfBirth = signal<Date | null>(null);
  legalRepresentativeFiscalCode = signal<string>('');
  legalRepresentativeFiscalCodeError = signal<string>('');

  // Active accordion panels
  activeIndexes = signal<string[]>([]);

  // Dropdown options
  streetTypeOptions = signal<DropdownOption[]>([]);
  companyTypeOptions = signal<DropdownOption[]>([]);
  provinceOptions = signal<DropdownOption[]>([]);
  regionOptions = signal<DropdownOption[]>([]);
  municipalityOptions = signal<DropdownOption[]>([]);

  private taxRegimeOptions = signal<DropdownOption[]>([]);
  fiscalRegimesOptions = computed(() =>
    sortTaxRegimeOptions(this.taxRegimeOptions(), this.preRegType()),
  );

  // Billing address geographic options
  invoiceProvinceOptions = signal<DropdownOption[]>([]);
  invoiceRegionOptions = signal<DropdownOption[]>([]);
  invoiceMunicipalityOptions = signal<DropdownOption[]>([]);

  // Person type options - loaded from API
  personTypeOptions = signal<DropdownOption[]>([]);

  // Store all supplier types for filtering
  private allSupplierTypes = signal<DropdownOption[]>([]);

  // Loading state for initial data fetch
  isLoadingData = signal<boolean>(false);

  // True when the API response says this professional has a parent (sede operativa)
  private hasParentFromApi = signal<boolean>(false);

  // Operational location mode: only "Dati Istituzionali Generali" is shown/validated.
  // In review mode (professionalIdInput set), rely on API data instead of wizard state.
  isOperationalLocationMode = computed(() =>
    this.professionalIdInput()
      ? this.hasParentFromApi()
      : this.professionalsWizardService.isOperationalLocationOnboarding()
  );

  isAxaDeutscheBankBodyshop = computed(() => this.preRegBodyShopType() === 'AXA_DEUTSCHE_BANK');

  // Form validation state - tracks if form has been submitted for inline error display
  formSubmitted = signal<boolean>(false);
  
  // Track which fields have been touched (blur event) for on-blur validation
  private touchedFields = new Set<string>();

  // Helper to check if a field has been touched or form submitted
  isFieldTouched(fieldName: string): boolean {
    return this.formSubmitted() || this.touchedFields.has(fieldName);
  }

  // Mark field as touched on blur
  markFieldTouched(fieldName: string): void {
    this.touchedFields.add(fieldName);
  }

  onRepresentativeMobileChange(number: string, prefix: string): void {
    if (this.isReadOnly()) {
      return;
    }
    this.mobilePhoneResponsible.set(number);
    this.mobilePhoneResponsiblePrefix.set(prefix);
    this.prefillClaimsNetworkPhone();
  }

  onClaimsNetworkPhoneChange(number: string, prefix: string): void {
    if (this.isReadOnly()) {
      return;
    }
    this.shouldPrefillClaimsNetworkPhone = false;
    this.claimsNetworkPhone.set(number);
    this.claimsNetworkPhonePrefix.set(prefix);
  }

  private prefillClaimsNetworkPhone(): void {
    if (!this.shouldPrefillClaimsNetworkPhone || this.isReadOnly()) {
      return;
    }
    this.claimsNetworkPhone.set(this.mobilePhoneResponsible());
    this.claimsNetworkPhonePrefix.set(this.mobilePhoneResponsiblePrefix());
  }

  /**
   * Save current form data to sessionStorage for recovery after login
   */
  private saveDraftToSession(): void {
    try {
      const formData = this.getFormData();
      sessionStorage.setItem(this.DRAFT_STORAGE_KEY, JSON.stringify(formData));
      console.log('💾 Draft saved to session storage');
    } catch (error) {
      console.error('❌ Error saving draft to session:', error);
    }
  }

  /**
   * Load draft data from sessionStorage and populate form
   */
  private loadDraftFromSession(): boolean {
    try {
      const savedData = sessionStorage.getItem(this.DRAFT_STORAGE_KEY);
      if (!savedData) {
        return false;
      }

      const data = JSON.parse(savedData);
      console.log('📂 Loading draft from session storage');
      
      // Populate all form fields from saved data
      this.populateFormFromData(data);
      
      // Clear the saved draft after loading
      this.clearDraftFromSession();
      
      // Show success message
      this.messageService.add({
        severity: 'success',
        summary: 'Bozza Ripristinata',
        detail: 'I dati del form sono stati ripristinati dalla sessione precedente.',
        life: 4000,
      });
      
      return true;
    } catch (error) {
      console.error('❌ Error loading draft from session:', error);
      this.clearDraftFromSession();
      return false;
    }
  }

  /**
   * Clear draft from sessionStorage
   */
  private clearDraftFromSession(): void {
    sessionStorage.removeItem(this.DRAFT_STORAGE_KEY);
  }

  /**
   * Handle missing professional ID by saving draft and redirecting to login
   */
  private handleMissingProfessionalId(): void {
    console.warn('⚠️ Professional ID missing - saving draft and redirecting to login');
    
    // Save current form data
    this.saveDraftToSession();
    
    // Redirect to login
    this.router.navigate(['/login']);
  }

  /**
   * Navigate back to progression page from read-only mode
   */
  backToProgression(): void {
    console.log('[General Data] Returning to progression');
    this.router.navigate(['/axa/professionals-registration-progress']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  ngOnInit(): void {
    console.log('🔵 [General Data] ngOnInit - Component initializing');
    
    // Load current registration step from backend (needed for read-only mode detection)
    const professionalId = this.getCurrentProfessionalId();
    if (professionalId) {
      this.professionalsWizardService.loadCurrentStep(professionalId);
      
      // Load both current step AND history to detect modification requests
      forkJoin({
        currentStep: this.registrationStepService.getCurrentStep(professionalId),
        history: this.registrationStepService.getStepHistory(professionalId)
      }).subscribe({
        next: ({ currentStep, history }) => {
          this.currentRegistrationStep.set(currentStep);
          this.stepHistory.set(history || []);
          
          console.log('📋 [General Data] Current registration step loaded:', currentStep);
          console.log('📋 [General Data] Step history loaded:', history?.length || 0, 'steps');
          
          // Check if there's a modification request in history
          if (currentStep.step === 'DATA_INPUT' && 
              currentStep.status === RegistrationStatus.USER_INPUT) {
            const hasModification = history?.some(s => 
              s.step === 'VERIFICATION' && 
              s.status === RegistrationStatus.MODIFICATION_REQUESTED
            );
            
            if (hasModification) {
              console.log('⚠️ [General Data] Modification request detected!');
              this.liveRegionMessage.set('Richiesta di modifica presente. Si prega di leggere i commenti del revisore.');
            }
          }
        },
        error: (err) => {
          console.error('❌ Error loading registration step or history:', err);
        }
      });
      
      // Preload completion status for all wizard steps to show correct state
      console.log('🔍 [General Data] Loading completion status for all wizard steps...');
      
      // Load step 0 (general data) status
      this.professionalsWizardService.preloadGeneralDataStatus(professionalId);
      
      // Load step 1 (specific data) status - will load once tipologia is available
      const tipologia = this.professionalsDataService.getTipologiaFiduciario();
      if (tipologia) {
        this.professionalsWizardService.preloadSpecificDataStatus(professionalId, tipologia);
      }
      // Preload step 2 as well, so its completion icon is correct before the user
      // opens the questionnaire page. The wizard service evaluates the individual
      // questionnaire, checklist and VRF statuses rather than the transient summary step.
      this.professionalsWizardService.preloadQuestionnaireStatus(professionalId);
    }
    
    // Set this step as active in the wizard
    this.professionalsWizardService.setStep(0);
    
   
    

    
    this.loadPersonTypeOptions();
    this.loadStreetTypeOptions();
    this.loadCompanyTypeOptions();
    this.loadTaxRegimeOptions();
    this.loadRegionsOptions();
    this.loadInvoiceRegionsOptions();
    // Pre-carica i prefissi telefonici dal BE: servono al parsing dei numeri
    // legacy (prefisso concatenato) in resolvePhoneParts.
    this.formDropdownService.getPhonePrefixes().subscribe({
      error: (err) => console.error('Errore caricamento prefissi telefonici:', err),
    });

 // Load data from backend (if professional ID exists)
    // This will also initialize the tipologia from API response
    this.loadProfessionalData();

        // Check if there's a saved draft from previous session (after login redirect)
    // This will override backend data if exists
    this.loadDraftFromSession();


  }

  /**
   * Load professional general data from backend
   */
  private loadProfessionalData(): void {
    const professionalId = this.getCurrentProfessionalId();
    if (!professionalId) {
      console.warn('⚠️ No professional ID found - skipping data load');
      return;
    }

    console.log('📥 Loading general data for professional:', professionalId);
    this.isLoadingData.set(true);

    this.professionalsService.getProfessionalGeneralData(professionalId)
      .pipe(
        catchError((error) => {
          console.error('❌ Error loading professional data:', error);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare i dati. Compila il form manualmente.',
            life: 5000,
          });
          return of(null);
        })
      )
      .subscribe((response) => {
        this.isLoadingData.set(false);

        if (!response || !response.data) {
          console.warn('⚠️ No data returned from API');
          return;
        }

        const data = response.data;
        console.log('✅ Professional data loaded:', data);

        // Populate form with data
        this.populateFormFromData(data);

        // If tipologia exists in service, preload specific data status
        const tipologia = this.professionalsDataService.getTipologiaFiduciario();
        if (tipologia) {
          console.log('📊 [General Data] Preloading specific data status for tipologia:', tipologia);
          this.professionalsWizardService.preloadSpecificDataStatus(professionalId, tipologia);
        }

        // In readonly mode (BO viewing professional) the wizard state is always fresh
        // (no in-memory/sessionStorage to preserve), so we can safely preload step 2.
        if (this.professionalIdInput()) {
          const resolvedType = tipologia || data.professionalType || '';
          const isNK = resolvedType === 'NK_BODYSHOP';
          if (isNK) {
            this.professionalsWizardService.preloadNKAttachmentsStatus(professionalId);
          } else {
            this.professionalsWizardService.preloadQuestionnaireStatus(professionalId);
          }
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Dati caricati',
          detail: 'I tuoi dati sono stati caricati con successo',
          life: 3000,
        });
      });
  }

  /**
   * Populate all form fields from data object
   * Used both for backend data loading and session draft recovery
   */
  private populateFormFromData(data: any): void {
    // In review mode, derive operational-location status from API data
    if (this.professionalIdInput()) {
      this.hasParentFromApi.set(!!data.parentProfessionalId);
    }

    const preregistrationFiscalCode =
      data.companyData?.legalOrPhysical === 'FISICA' ? data.companyData?.fiscalCode || '' : '';

    const preRegistrationFirstName =
      data.firstname || data.companyData?.firstname || '';
    const preRegistrationLastName =
      data.lastname || data.companyData?.lastname || '';
    this.preRegFirstName.set(preRegistrationFirstName);
    this.preRegLastName.set(preRegistrationLastName);
    this.firstname.set(preRegistrationFirstName);
    this.lastname.set(preRegistrationLastName);

    // Populate companyData
    if (data.companyData) {
      this.preRegFirstName.set(data.companyData.firstname || data.firstname || '');
      this.preRegLastName.set(data.companyData.lastname || data.lastname || '');
      this.businessName.set(data.companyData.businessName || data.businessName || '');
      this.vatNumber.set(data.companyData.vatNumber || '');
      this.legalOrPhysical.set(data.companyData.legalOrPhysical || null);
      this.companyType.set(data.companyData.companyType || '');
      this.fiscalCode.set(data.companyData.fiscalCode || '');
    } else if (data.businessName) {
      this.businessName.set(data.businessName || '');
    }

    // Populate legalAddress
    if (data.legalAddress) {
      this.region.set(data.legalAddress.region || '');
      this.province.set(data.legalAddress.province || '');
      this.municipality.set(data.legalAddress.municipality || '');
      this.zipCode.set(data.legalAddress.zipCode || '');
      this.streetType.set(data.legalAddress.streetType || '');
      this.streetName.set(data.legalAddress.streetName || '');
      this.streetNumber.set(data.legalAddress.streetNumber || null);

      // Load province/municipality options based on loaded region/province
      if (data.legalAddress.region) {
        this.geographyService.getProvinces(data.legalAddress.region).subscribe((provinces) => {
          this.provinceOptions.set((provinces || []).map(p => ({ label: p.name, value: p.code })));
        });

        if (data.legalAddress.province) {
          this.geographyService.getMunicipalities(data.legalAddress.province).subscribe((municipalities) => {
            this.municipalityOptions.set((municipalities || []).map(m => ({ label: m.name, value: m.code })));
          });
        }
      }
    }

    this.invoiceAddressSameAsLegalAddress.set(
      data.bankDetails?.invoiceAddressSameAsLegalAddress !== false,
    );

    const registrationEmail = data.registrationEmail || '';

    // Populate contactInfo
    if (data.contactInfo) {
      // Contatti istituzionali
      this.institutionalEmailSameAsRegistrationEmail.set(
        data.contactInfo.institutionalEmailSameAsRegistrationEmail !== false,
      );
      this.email.set(
        this.institutionalEmailSameAsRegistrationEmail()
          ? registrationEmail
          : data.contactInfo.email || '',
      );
      this.certifiedEmail.set(data.contactInfo.certifiedEmail || '');
      const mobilePhoneParts = this.resolvePhoneParts(
        data.contactInfo.mobilePhone,
        data.contactInfo.mobilePhonePrefix,
      );
      this.mobilePhone.set(mobilePhoneParts.number);
      this.mobilePhonePrefix.set(mobilePhoneParts.prefix);
      const telephoneParts = this.resolvePhoneParts(
        data.contactInfo.telephone,
        data.contactInfo.telephonePrefix,
      );
      this.telephone.set(telephoneParts.number);
      this.telephonePrefix.set(telephoneParts.prefix);
      this.website.set(data.contactInfo.website || '');

      // Contatti rappresentante legale
      this.responsibleEmailSameAsRegistrationEmail.set(
        data.contactInfo.responsibleEmailSameAsRegistrationEmail !== false,
      );
      this.responsibleEmail.set(
        this.responsibleEmailSameAsRegistrationEmail()
          ? registrationEmail
          : data.contactInfo.responsibleEmail || '',
      );
      const mobilePhoneResponsibleParts = this.resolvePhoneParts(
        data.contactInfo.mobilePhoneResponsible,
        data.contactInfo.mobilePhoneResponsiblePrefix,
      );
      this.mobilePhoneResponsible.set(mobilePhoneResponsibleParts.number);
      this.mobilePhoneResponsiblePrefix.set(mobilePhoneResponsibleParts.prefix);
      const ownerTelephoneParts = this.resolvePhoneParts(
        data.contactInfo.ownerTelephone,
        data.contactInfo.ownerTelephonePrefix,
      );
      this.ownerTelephone.set(ownerTelephoneParts.number);
      this.ownerTelephonePrefix.set(ownerTelephoneParts.prefix);
      const claimsNetworkPhoneParts = this.resolvePhoneParts(
        data.contactInfo.claimsNetworkPhone,
        data.contactInfo.claimsNetworkPhonePrefix,
      );
      this.claimsNetworkPhone.set(claimsNetworkPhoneParts.number);
      this.claimsNetworkPhonePrefix.set(claimsNetworkPhoneParts.prefix);
      this.shouldPrefillClaimsNetworkPhone = !claimsNetworkPhoneParts.number;
      this.prefillClaimsNetworkPhone();
    } else {
      this.institutionalEmailSameAsRegistrationEmail.set(true);
      this.email.set(registrationEmail);
      this.responsibleEmailSameAsRegistrationEmail.set(true);
      this.responsibleEmail.set(registrationEmail);
    }

    // Populate bankDetails
    const storedInvoiceVatNumber = data.bankDetails?.vatNumber || '';
    if (this.isMissingMeaningfulText(this.vatNumber())) {
      // No company VAT: hide the question, treat as different, keep field editable
      this.invoiceVatNumberSameAsVatNumber.set(false);
      this.invoiceVatNumber.set(storedInvoiceVatNumber);
    } else {
      const isDifferent =
        this.hasMeaningfulText(storedInvoiceVatNumber) && storedInvoiceVatNumber !== this.vatNumber();
      this.invoiceVatNumberSameAsVatNumber.set(!isDifferent);
      this.invoiceVatNumber.set(isDifferent ? storedInvoiceVatNumber : this.vatNumber());
    }
    const storedInvoiceParty = normalizeTrimmedText(data.bankDetails?.invoiceParty);
    const defaultInvoiceParty = this.getDefaultInvoiceParty();
    this.invoicePartyAutoFillValue =
      !storedInvoiceParty || storedInvoiceParty === normalizeTrimmedText(this.businessName())
        ? storedInvoiceParty || defaultInvoiceParty
        : null;
    this.invoiceParty.set(storedInvoiceParty || defaultInvoiceParty);

    if (data.bankDetails) {
      this.fiscalRegime.set(data.bankDetails.fiscalRegime || '');
      this.iban.set(formatIban(data.bankDetails.iban || ''));
      this.bankInstitutionName.set(data.bankDetails.bankInstitutionName || '');
      this.bankInstitutionBranch.set(data.bankDetails.bankInstitutionBranch || '');
      this.accountOwner.set(data.bankDetails.accountOwner || '');
      this.swift.set(data.bankDetails.swift || '');

      // Populate invoice address
      if (data.bankDetails.address) {
        this.invoiceRegion.set(data.bankDetails.address.region || '');
        this.invoiceProvince.set(data.bankDetails.address.province || '');
        this.invoiceMunicipality.set(data.bankDetails.address.municipality || '');
        this.invoiceZipCode.set(data.bankDetails.address.zipCode || '');
        this.invoiceStreetType.set(data.bankDetails.address.streetType || '');
        this.invoiceAddress.set(data.bankDetails.address.streetName || '');
        this.invoiceStreetNumber.set(data.bankDetails.address.streetNumber || null);

        // Load invoice province/municipality options
        if (data.bankDetails.address.region) {
          this.geographyService.getProvinces(data.bankDetails.address.region).subscribe((provinces) => {
            this.invoiceProvinceOptions.set((provinces || []).map(p => ({ label: p.name, value: p.code })));
          });

          if (data.bankDetails.address.province) {
            this.geographyService.getMunicipalities(data.bankDetails.address.province).subscribe((municipalities) => {
              this.invoiceMunicipalityOptions.set((municipalities || []).map(m => ({ label: m.name, value: m.code })));
            });
          }
        }
      }

      if (this.invoiceAddressSameAsLegalAddress()) {
        this.copyLegalAddressToInvoice();
      }
    }

    // Populate legalRepresentative
    this.legalRepresentativeFiscalCode.set(
      data.legalRepresentative?.fiscalCode || preregistrationFiscalCode,
    );

    if (data.legalRepresentative) {
      this.firstname.set(data.legalRepresentative.firstname || preRegistrationFirstName);
      this.lastname.set(data.legalRepresentative.lastname || preRegistrationLastName);
      this.birthMunicipality.set(data.legalRepresentative.birthMunicipality || '');
      this.legalRepresentativeFiscalCode.set(data.legalRepresentative.fiscalCode || '');

      // Convert ISO date string to Date object
      if (data.legalRepresentative.dateOfBirth) {
        this.dateOfBirth.set(new Date(data.legalRepresentative.dateOfBirth));
      }
    }

    // ✨ PRE-REGISTRATION READONLY DATA
    // All fields come directly from GET /general-data response
    // (BE: ProfessionalGeneralDataResponse includes firstname/lastname/registrationEmail/bodyShopType)
    this.preRegType.set(data.professionalType || '');
    this.preRegFirstName.set(data.firstname || '');
    this.preRegLastName.set(data.lastname || '');
    this.preRegEmail.set(data.registrationEmail || '');
    // bodyShopType is set below inside getBodyshopData() callback (for BODYSHOP type)
    // but also available directly from general-data if already present
    if (data.bodyShopType) {
      this.preRegBodyShopType.set(data.bodyShopType);
      const bodyShopTypeMap: Record<string, string> = {
        'STANDARD': 'STANDARD - Carrozzeria Standard',
        'ARS': 'ARS - AXA Repair Services',
        'RCI': 'RCI - Rete Carrozzerie Italiane',
        'NOBIS': 'NOBIS - Carrozzeria NOBIS',
        'AXA_DEUTSCHE_BANK': 'AXA Deutsche Bank',
      };
      this.preRegBodyShopTypeLabel.set(bodyShopTypeMap[data.bodyShopType] || data.bodyShopType);
    }

    // Decode Categoria Fiduciario label
    if (data.professionalType) {
      this.formDropdownService.decodeProfessionalType(data.professionalType).subscribe(label => {
        this.preRegTypeLabel.set(label);
      });
    }

    // ✨ SAVE PROFESSIONAL TYPE FROM API RESPONSE
    // This is critical for navigation to specific-data component
    if (data.professionalType) {
      console.log('💾 [General Data] Saving professionalType from API:', data.professionalType);
      console.log('💾 [General Data] professionalSubType from API:', data.professionalSubType);
      console.log('💾 [General Data] Full data object:', data);
      
      // Map API professionalType to tipologiaFiduciario
      const tipologiaFiduciario = data.professionalType as any;
      let tipologiaCarrozzeria = data.professionalSubType || null;
      
      // 🚗 If BODYSHOP, fetch bodyshop-specific data to get professionalSubType
      if (tipologiaFiduciario === 'BODYSHOP') {
        console.log('🚗 [General Data] BODYSHOP detected - fetching bodyshop data for subtype...');
        const professionalId = this.getCurrentProfessionalId();
        
        if (professionalId) {
          this.professionalsService.getBodyshopData(professionalId)
            .pipe(catchError((error) => {
              console.error('❌ [General Data] Error loading bodyshop data:', error);
              return of(null);
            }))
            .subscribe((bodyshopResponse) => {
              if (bodyshopResponse?.data) {
                // Extract subtype from bodyshop response (bodyShopType is the correct field)
                let bodyshopSubType = bodyshopResponse.data.bodyShopType;
                console.log('🚗 [General Data] BODYSHOP bodyShopType from API:', bodyshopSubType);
                console.log('🚗 [General Data] Full bodyshop response:', bodyshopResponse.data);

                // Update service with correct subtype
                this.professionalsDataService.initializeProfessionalsData(
                  tipologiaFiduciario,
                  bodyshopSubType
                );
                
                console.log('✅ [General Data] BODYSHOP subtype updated in service:', bodyshopSubType);
              } else {
                console.warn('⚠️ [General Data] No bodyshop data returned');
              }
            });
        }
      }
      
      console.log('💾 [General Data] Initializing service with tipologiaFiduciario:', tipologiaFiduciario);
      console.log('💾 [General Data] Initializing service with tipologiaCarrozzeria:', tipologiaCarrozzeria);
      console.log('💾 [General Data] Types:', {
        tipologiaFiduciarioType: typeof tipologiaFiduciario,
        tipologiaCarrozzeriaType: typeof tipologiaCarrozzeria
      });
      
      this.professionalsDataService.initializeProfessionalsData(
        tipologiaFiduciario,
        tipologiaCarrozzeria
      );
      
      // Verify it was saved correctly
      const savedTipologia = this.professionalsDataService.getTipologiaFiduciario();
      const savedCarrozzeria = this.professionalsDataService.getTipologiaCarrozzeria();
      console.log('✅ [General Data] Verified saved tipologia:', savedTipologia);
      console.log('✅ [General Data] Verified saved carrozzeria:', savedCarrozzeria);
    } else {
      console.warn('⚠️ [General Data] No professionalType in API response!');
    }

    // Check section status and mark step as completed ONLY if FILLED
    if (data.generalDataSectionStatus === 'FILLED') {
      console.log('✅ General data section is FILLED - marking step as completed');
      this.professionalsWizardService.markStepCompleted(0, true);
    } else {
      console.log('⚠️ General data section is NOT filled (status:', data.generalDataSectionStatus, ') - marking step as NOT completed');
      this.professionalsWizardService.markStepCompleted(0, false);
    }
    
    // Save wizard state to persist the completed status
    this.professionalsWizardService.saveState();
  }

  onAccordionChange(value: string | number | string[] | number[] | null | undefined): void {
    if (Array.isArray(value)) {
      this.activeIndexes.set(value as string[]);
    }
  }

  // Tax Code validation
  isValidVatNumber(value: string): boolean {
    return !this.hasMeaningfulText(value) || /^\d{11}$/.test(value.trim());
  }

  validateTaxCode(value: string, errorSignal: any): boolean {
    if (this.isMissingMeaningfulText(value)) {
      errorSignal.set('');
      return true;
    }

    value = normalizeTrimmedText(value).toUpperCase();

    // Check for company tax code (11 digits)
    if (/^\d{11}$/.test(value)) {
      errorSignal.set('');
      return true;
    }

    // Check for individual tax code (16 alphanumeric characters)
    if (/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(value)) {
      // Additional validation: month code
      const monthCode = value.charAt(8);
      const validMonths = 'ABCDEHLMPRST';
      if (validMonths.includes(monthCode)) {
        errorSignal.set('');
        return true;
      }
    }

    errorSignal.set(
      'Formato Codice Fiscale non valido (deve essere 16 caratteri alfanumerici o 11 cifre numeriche)',
    );
    return false;
  }

  onTaxCodeChange(value: string): void {
    const normalizedValue = this.normalizeUppercase(value);
    this.fiscalCode.set(normalizedValue);
    this.validateTaxCode(normalizedValue, this.fiscalCodeError);
  }

  onLegalRepTaxCodeChange(value: string): void {
    const normalizedValue = this.normalizeUppercase(value);
    this.legalRepresentativeFiscalCode.set(normalizedValue);
    this.validateTaxCode(normalizedValue, this.legalRepresentativeFiscalCodeError);
  }

  onIbanInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const caretBeforeFormatting = input.selectionStart ?? rawValue.length;
    const valueBeforeCaret = rawValue.slice(0, caretBeforeFormatting);

    if (hasInvalidIbanCharacters(rawValue)) {
      const uppercaseValue = rawValue.toUpperCase();
      this.iban.set(uppercaseValue);
      input.value = uppercaseValue;
      this.restoreIbanCaret(input, caretBeforeFormatting);
      return;
    }

    const normalizedValue = normalizeIban(rawValue);
    const normalizedCaret = normalizeIban(valueBeforeCaret).length;
    const formattedValue = formatIban(normalizedValue);

    this.iban.set(formattedValue);
    input.value = formattedValue;
    queueMicrotask(() => {
      if (document.activeElement !== input) {
        return;
      }

      input.setSelectionRange(
        this.getFormattedIbanCaret(normalizedCaret, formattedValue),
        this.getFormattedIbanCaret(normalizedCaret, formattedValue),
      );
    });
  }

  private restoreIbanCaret(input: HTMLInputElement, caret: number): void {
    queueMicrotask(() => {
      if (document.activeElement === input) {
        input.setSelectionRange(caret, caret);
      }
    });
  }

  private getFormattedIbanCaret(normalizedCaret: number, formattedValue: string): number {
    if (normalizedCaret <= 0) {
      return 0;
    }

    let normalizedCharacters = 0;
    for (let index = 0; index < formattedValue.length; index++) {
      if (formattedValue[index] !== ' ') {
        normalizedCharacters++;
      }

      if (normalizedCharacters === normalizedCaret) {
        return index + 1;
      }
    }

    return formattedValue.length;
  }

  onIbanBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;

    if (hasInvalidIbanCharacters(rawValue)) {
      const uppercaseValue = rawValue.toUpperCase();
      this.iban.set(uppercaseValue);
      input.value = uppercaseValue;
      return;
    }

    const formattedValue = formatIban(rawValue);
    this.iban.set(formattedValue);
    input.value = formattedValue;
  }

  onSwiftChange(value: string): void {
    this.swift.set(this.normalizeUppercase(value).replace(/\s+/g, ''));
  }

  private isInvalidSwiftFormat(value: string): boolean {
    const normalizedValue = normalizeTrimmedText(value);
    if (!normalizedValue) {
      return false;
    }

    return !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(normalizedValue);
  }

  private normalizeUppercase(value: string | null | undefined): string {
    return normalizeTrimmedText(value).toUpperCase();
  }

  /**
   * Torna alla pagina degli step di registrazione
   */
  backToSteps(): void {
    this.router.navigate(['/axa/professionals-registration-progress']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.professionalsWizardService.consumeNavigationWithoutWarning()) {
      return true;
    }

    if (this.allowNextNavigation) {
      this.allowNextNavigation = false;
      return true;
    }

    if (!this.shouldWarnBeforeLeaving()) {
      return true;
    }

    return new Observable<boolean>((observer) => {
      this.confirmDialogService.confirmAction(
        this.UNSAVED_CHANGES_MESSAGE,
        () => {
          observer.next(true);
          observer.complete();
        },
        'Attenzione',
        'Esci senza salvare',
        'Resta nella pagina'
      );
    });
  }

  private shouldWarnBeforeLeaving(): boolean {
    return !this.isReadOnly();
  }

  private bypassUnsavedChangesWarningForNextNavigation(): void {
    this.allowNextNavigation = true;
  }

  onSaveDraft(): void {
    if (this.ibanInvalidCharacterError()) {
      this.markFieldTouched('iban');
      this.messageService.add({
        severity: 'error',
        summary: 'IBAN non valido',
        detail: 'L\'IBAN può contenere solo lettere, numeri e spazi.',
        life: 4000,
      });
      return;
    }

    if (this.accountOwnerMismatch()) {
      this.showAccountOwnerMismatchError();
      return;
    }

    const formData = this.getFormData();
    const professionalId = this.getCurrentProfessionalId();

    if (!professionalId) {
      this.handleMissingProfessionalId();
      return;
    }

    // Add section status for draft
    const payload = {
      ...formData,
      generalDataSectionStatus: 'DRAFT'
    };

    console.log('Saving draft...', payload);

    // Call PATCH API to save draft
    this.professionalsService.updateProfessionalGeneralData(professionalId, payload)
      .pipe(
        catchError((error) => {
          console.error('❌ Error saving draft:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: this.getSaveErrorDetail(error, 'Impossibile salvare la bozza. Riprova più tardi.'),
            life: 4000,
          });
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response) {
          console.log('✅ Draft saved successfully:', response);

          // Save wizard state without marking as completed
          this.professionalsWizardService.saveState();

          // Announce to screen readers
          this.liveRegionMessage.set('Bozza salvata con successo');
          setTimeout(() => this.liveRegionMessage.set(''), 3000);

          // Show success message
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Bozza salvata correttamente',
            life: 4000,
          });
        }
      });
  }

  onSaveAndContinue(): void {
    // Mark form as submitted to show inline validation errors
    this.formSubmitted.set(true);
    
    // Validate form before proceeding
    const validationErrors = this.validateForm();
    
    if (validationErrors.length > 0) {
      this.validationErrorSections.set(this.buildValidationErrorSections(validationErrors));
      
      // Announce to screen readers
      this.liveRegionMessage.set('Compila tutti i campi obbligatori');
      setTimeout(() => this.liveRegionMessage.set(''), 3000);
      
      return; // Stop execution
    }

    this.validationErrorSections.set([]);

    if (this.isInvoicePartyMismatch()) {
      this.confirmDialogService.confirmAction(
        'Confermi di voler salvare nonostante "Ragione Sociale" in Dati Istituzionali e "Ragione Sociale Fattura" in Recapiti Bancari e Fiscali non corrispondano?',
        () => this.saveAndContinue(),
        'Conferma salvataggio',
        'Conferma',
        'Annulla',
      );
      return;
    }

    this.saveAndContinue();
  }

  private saveAndContinue(): void {
    
    const formData = this.getFormData();
    const professionalId = this.getCurrentProfessionalId();

    if (!professionalId) {
      this.handleMissingProfessionalId();
      return;
    }

    // Add section status for filled
    const payload = {
      ...formData,
      generalDataSectionStatus: 'FILLED'
    };

    // Call PATCH API to save data
    this.professionalsService.updateProfessionalGeneralData(professionalId, payload)
      .pipe(
        catchError((error) => {
          console.error('❌ Error saving data:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: this.getSaveErrorDetail(error, 'Impossibile salvare i dati. Riprova più tardi.'),
            life: 4000,
          });
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response) {
          console.log('✅ Data saved successfully:', response);

          // Mark step 0 as completed and enable next step
          this.professionalsWizardService.markStepCompleted(0, true);
          this.professionalsWizardService.saveState();

          // Announce success to screen readers
          this.liveRegionMessage.set('Dati generali salvati con successo');

          // Show success message
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Dati generali salvati correttamente',
            life: 3000,
          });

          // In operational location mode navigate directly to specific data (step 1)
          if (this.isOperationalLocationMode()) {
            this.bypassUnsavedChangesWarningForNextNavigation();
            this.router.navigate(['/axa/professionals-specific-data']).then(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            });
            return;
          }

          // Verify tipologia is in service (should be there from API load)
          const currentTipologia = this.professionalsDataService.getTipologiaFiduciario();
          console.log('🔍 [General Data] Tipologia in service before navigation:', currentTipologia);
          
          if (!currentTipologia) {
            console.error('❌ [General Data] No tipologia in service! This should not happen if API loaded correctly.');
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Tipologia professionale non disponibile. Ricarica la pagina.',
              life: 5000,
            });
            return;
          }

          // Preload specific data status to determine if step 1 is completed
          console.log('📊 [General Data] Preloading specific data status for tipologia:', currentTipologia);
          this.professionalsWizardService.preloadSpecificDataStatus(professionalId, currentTipologia);

          // Navigate to next step: skip "Dati Specifici" (step 1) for NETWORK type
          const nextStepId = currentTipologia === 'NETWORK' ? 2 : 1;
          const nextStep = this.professionalsWizardService.steps().find(s => s.id === nextStepId);
          if (nextStep) {
            console.log('🚀 [General Data] Navigating to:', nextStep.routerLink);
            this.bypassUnsavedChangesWarningForNextNavigation();
            this.router.navigate([nextStep.routerLink]).then(() => {
              // Scroll to top after navigation
              window.scrollTo({ top: 0, behavior: 'smooth' });
            });
          }
        }
      });
  }

  private showAccountOwnerMismatchError(): void {
    const error = this.getAccountOwnerMismatchErrorLabel();
    this.markFieldTouched('accountOwner');
    this.validationErrorSections.set(this.buildValidationErrorSections([error]));
    this.messageService.add({
      severity: 'error',
      summary: 'Intestatario non valido',
      detail: this.getAccountOwnerMismatchMessage(),
      life: 5000,
    });
    this.liveRegionMessage.set(this.getAccountOwnerMismatchMessage());
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  private getAccountOwnerMismatchErrorLabel(): string {
    return `Intestatario C/C (${this.getAccountOwnerMismatchMessage()})`;
  }

  getAccountOwnerMismatchMessage(): string {
    if (this.legalOrPhysical() === 'FISICA') {
      return "L'intestatario non corrisponde con il Cognome e Nome titolare inseriti.";
    }

    return this.isAxaDeutscheBankBodyshop()
      ? "L'intestatario non corrisponde alla Ragione Sociale e al Tipo Societ\u00e0 inseriti nei Dati Istituzionali Generali."
      : "L'intestatario non corrisponde alla Ragione Sociale Fattura inserita.";
  }

  private getExpectedAccountOwner(): string {
    if (this.legalOrPhysical() === 'FISICA') {
      return [this.lastname(), this.firstname()]
        .map(value => normalizeTrimmedText(value))
        .filter(Boolean)
        .join(' ');
    }

    if (this.legalOrPhysical() === 'GIURIDICA') {
      return this.isAxaDeutscheBankBodyshop()
        ? this.getExpectedBusinessNameWithCompanyType()
        : normalizeTrimmedText(this.invoiceParty());
    }

    return '';
  }

  private getExpectedBusinessNameWithCompanyType(): string {
    // Ditta individuale (persona fisica): nessuna sigla tipo società, solo Cognome Nome titolare
    if (this.legalOrPhysical() !== 'GIURIDICA') {
      return [this.lastname(), this.firstname()]
        .map(value => normalizeTrimmedText(value))
        .filter(Boolean)
        .join(' ');
    }

    const businessName = normalizeTrimmedText(this.businessName());
    const companyTypeLabel =
      this.allSupplierTypes().find(option => option.value === this.companyType())?.label || this.companyType();
    const companyTypeSuffix = companyTypeLabel.match(/\(([^()]+)\)\s*$/)?.[1]?.trim() || companyTypeLabel;

    return [businessName, companyTypeSuffix].filter(Boolean).join(' ');
  }

  private normalizeAccountOwnerValue(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('it-IT')
      .replace(/[^a-z0-9]/g, '');
  }

  private isInvoicePartyMismatch(): boolean {
    const invoiceParty = normalizeTrimmedText(this.invoiceParty());
    const expectedInvoiceParty = this.getExpectedBusinessNameWithCompanyType();

    return !!invoiceParty && !!expectedInvoiceParty &&
      this.normalizeAccountOwnerValue(invoiceParty) !== this.normalizeAccountOwnerValue(expectedInvoiceParty);
  }

  private getSaveErrorDetail(error: unknown, fallback: string): string {
    const response = error as { error?: { message?: string } };
    return response?.error?.message || fallback;
  }

  private buildValidationErrorSections(validationErrors: string[]): ValidationErrorSection[] {
    const sectionDefinitions: { title: string; labels: string[] }[] = [
      {
        title: 'Dati Istituzionali Generali',
        labels: ['Persona Fisica/Giuridica', 'Ragione Sociale', 'Partita IVA', 'Codice Fiscale', 'Tipo Società'],
      },
      {
        title: 'Indirizzo Sede Legale',
        labels: ['Tipo Via', 'Nome Via', 'Civico', 'CAP', 'Regione', 'Provincia', 'Comune'],
      },
      {
        title: 'Contatti Istituzionali',
        labels: [
          'Email Istituzionale', 'Pec Istituzionale', 'Cellulare Istituzionale',
          'Telefono Istituzionale', 'Sito Internet Istituzionale',
        ],
      },
      {
        title: 'Anagrafica Rappresentante Legale',
        labels: [
          'Nome Rappresentante', 'Cognome Rappresentante', 'Codice Fiscale Rappresentante',
          'Comune di Nascita', 'Data di Nascita',
        ],
      },
      {
        title: 'Contatti Rappresentante Legale',
        labels: [
          'Email Rappresentante Legale', 'Cellulare Rappresentante Legale',
          'Telefono Rappresentante Legale',
          'Numero di cellulare/telefono riservato alla rete liquidativa',
        ],
      },
      {
        title: 'Recapiti Bancari e Fiscali',
        labels: [
          'Ragione Sociale Fattura', 'Partita IVA Fattura', 'Tipo Via Fatturazione',
          'Indirizzo Fattura', 'Civico Fatturazione', 'CAP Fatturazione',
          'Regione Fatturazione', 'Provincia Fatturazione', 'Comune Fatturazione',
          'Regime Fiscale', 'IBAN', 'SWIFT', 'Istituto di Credito', 'Filiale', 'Intestatario C/C',
        ],
      },
    ];

    const groupedErrors = sectionDefinitions.map(section => ({
      title: section.title,
      errors: validationErrors.filter(error => {
        const normalizedError = error.replace(/\u00a0/g, ' ').trim();
        return section.labels.some(label =>
          normalizedError === label ||
          normalizedError.startsWith(`${label} (`) ||
          normalizedError.startsWith(`${label} #`),
        );
      }),
    }));

    return validationSectionsFromMessages(groupedErrors, error => this.getFieldIdForValidationError(error) ?? undefined);
  }

    private getFieldIdForValidationError(error: string): string | null {
      const normalizedError = error.replace(/\u00a0/g, ' ').trim();

      const fieldIds: Record<string, string> = {
        'Persona Fisica/Giuridica': 'persona-giuridica',
        'Tipo Società': 'tipo-societa',
        'Ragione Sociale': 'ragione-sociale',
        'Partita IVA': 'partita-iva',
        'Codice Fiscale': 'codice-fiscale',
        'Tipo Via': 'tipo-via',
        'Nome Via': 'nome-via',
        Civico: 'numero',
        CAP: 'cap',
        Regione: 'regione',
        Provincia: 'provincia',
        Comune: 'comune',
        'Email Istituzionale': 'email-istituzionale',
        'Pec Istituzionale': 'pec-istituzionale',
        'Cellulare Istituzionale': 'cellulare-istituzionale',
        'Telefono Istituzionale': 'telefono-istituzionale',
        'Sito Internet Istituzionale': 'sito-internet-istituzionale',
        'Email Rappresentante Legale': 'email-rappresentante',
        'Cellulare Rappresentante Legale': 'cellulare-rappresentante',
        'Telefono Rappresentante Legale': 'telefono-rappresentante',
        'Numero di cellulare/telefono riservato alla rete liquidativa': 'claims-network-phone-rappresentante',
        'Ragione Sociale Fattura': 'ragione-sociale-fattura',
        'Partita IVA Fattura': 'partita-iva-fattura',
        'Tipo Via Fatturazione': 'tipo-via-fatturazione',
        'Indirizzo Fattura': 'indirizzo-fattura',
        'Civico Fatturazione': 'numero-fatturazione',
        'CAP Fatturazione': 'cap-fatturazione',
        'Regione Fatturazione': 'regione-fatturazione',
        'Provincia Fatturazione': 'provincia-fatturazione',
        'Comune Fatturazione': 'comune-fatturazione',
        'Regime Fiscale': 'regime-fiscale',
        IBAN: 'iban',
        SWIFT: 'swift',
        'Istituto di Credito': 'istituto-credito',
        Filiale: 'filiale-di',
        'Intestatario C/C': 'intestatario-cc',
        'Nome Rappresentante': 'nome-rappresentante',
        'Cognome Rappresentante': 'cognome-rappresentante',
        'Comune di Nascita': 'comune-nascita',
        'Data di Nascita': 'data-nascita',
        'Codice Fiscale Rappresentante': 'codice-fiscale-rappresentante',
      };

      const matchingLabel = Object.keys(fieldIds).find(
        (label) => normalizedError === label || normalizedError.startsWith(`${label} (`),
      );

      return matchingLabel ? fieldIds[matchingLabel] : null;
    }

  /**
   * Validates all required fields in the form
   * @returns Array of field names that are missing or invalid
   */
  private validateForm(): string[] {
    const errors: string[] = [];

    // Company Data
    if (!this.legalOrPhysical()) {
      errors.push('Persona Fisica/Giuridica');
    }
    if (this.isMissingMeaningfulText(this.businessName())) {
      errors.push('Ragione Sociale');
    }
    if (this.legalOrPhysical() === 'FISICA') {
      if (this.isMissingMeaningfulText(this.fiscalCode())) {
        errors.push('Codice Fiscale');
      } else if (this.fiscalCodeError()) {
        errors.push('Codice Fiscale (formato non valido)');
      }
    } else if (this.legalOrPhysical() === 'GIURIDICA') {
      if (this.isMissingMeaningfulText(this.vatNumber())) {
        errors.push('Partita IVA');
      } else if (!this.isValidVatNumber(this.vatNumber())) {
        errors.push('Partita IVA (formato non valido)');
      }
    } else if (this.hasMeaningfulText(this.vatNumber()) && !this.isValidVatNumber(this.vatNumber())) {
      errors.push('Partita IVA (formato non valido)');
    }
    if (!this.companyType()) {
      errors.push('Tipo Società ');
    }

    // In operational location mode, only Dati Istituzionali Generali is shown/required
    if (this.isOperationalLocationMode()) {
      return errors;
    }

    // Structure Address
    if (!this.streetType()) {
      errors.push('Tipo Via');
    }
    if (this.isMissingMeaningfulText(this.streetName())) {
      errors.push('Nome Via');
    }
    if (!this.streetNumber()) {
      errors.push('Civico');
    }
    if (!this.zipCode()) {
      errors.push('CAP');
    }
    if (!this.region()) {
      errors.push('Regione');
    }
    if (!this.province()) {
      errors.push('Provincia');
    }
    if (!this.municipality()) {
      errors.push('Comune');
    }

    // Contatti Istituzionali
    if (this.isMissingMeaningfulText(this.email())) {
      errors.push('Email Istituzionale');
    } else if (!this.isValidEmail(this.email())) {
      errors.push('Email Istituzionale (formato non valido)');
    }
    if (this.isMissingMeaningfulText(this.certifiedEmail())) {
      errors.push('Pec Istituzionale');
    } else if (!this.isValidEmail(this.certifiedEmail())) {
      errors.push('Pec Istituzionale (formato non valido)');
    }
    errors.push(...this.validatePhoneField(this.mobilePhone(), 'Cellulare Istituzionale', false));
    errors.push(...this.validatePhoneField(this.telephone(), 'Telefono Istituzionale', true));

    // Contatti Rappresentante Legale
    if (this.isMissingMeaningfulText(this.responsibleEmail())) {
      errors.push('Email Rappresentante Legale');
    } else if (!this.isValidEmail(this.responsibleEmail())) {
      errors.push('Email Rappresentante Legale (formato non valido)');
    }
    errors.push(
      ...this.validatePhoneField(this.mobilePhoneResponsible(), 'Cellulare Rappresentante Legale', true),
    );
    errors.push(
      ...this.validatePhoneField(this.ownerTelephone(), 'Telefono Rappresentante Legale', false),
    );
    errors.push(
      ...this.validatePhoneField(this.claimsNetworkPhone(), 'Numero di cellulare/telefono riservato alla rete liquidativa', false),
    );

    // Banking and Tax Details
    if (!this.isAxaDeutscheBankBodyshop()) {
      if (this.isMissingMeaningfulText(this.invoiceParty())) {
        errors.push('Ragione Sociale Fattura');
      }
      if (!this.invoiceStreetType()) {
        errors.push('Tipo Via Fatturazione');
      }
      if (this.isMissingMeaningfulText(this.invoiceAddress())) {
        errors.push('Indirizzo Fattura');
      }
      if (!this.invoiceStreetNumber()) {
        errors.push('Civico Fatturazione');
      }
      if (!this.invoiceZipCode()) {
        errors.push('CAP Fatturazione');
      }
      if (!this.invoiceRegion()) {
        errors.push('Regione Fatturazione');
      }
      if (!this.invoiceProvince()) {
        errors.push('Provincia Fatturazione');
      }
      if (!this.invoiceMunicipality()) {
        errors.push('Comune Fatturazione');
      }
      if (this.isMissingMeaningfulText(this.invoiceVatNumber())) {
        errors.push('Partita IVA Fattura');
      }
    }
    if (this.isMissingMeaningfulText(this.fiscalRegime())) {
      errors.push('Regime Fiscale');
    }
    if (this.isMissingMeaningfulText(this.iban())) {
      errors.push('IBAN');
    } else if (this.ibanFormatError()) {
      errors.push('IBAN');
    }
    if (this.isMissingMeaningfulText(this.bankInstitutionName())) {
      errors.push('Istituto di Credito');
    }
    if (this.isMissingMeaningfulText(this.bankInstitutionBranch())) {
      errors.push('Filiale');
    }
    if (this.isMissingMeaningfulText(this.accountOwner())) {
      errors.push('Intestatario C/C');
    } else if (this.accountOwnerMismatch()) {
      errors.push(this.getAccountOwnerMismatchErrorLabel());
    }
    if (!this.isAxaDeutscheBankBodyshop()) {
      if (this.isMissingMeaningfulText(this.swift())) {
        errors.push('SWIFT');
      } else if (this.swiftFormatError()) {
        errors.push('SWIFT (formato non valido)');
      }
    }

    // Legal Representative Data
    if (this.isMissingMeaningfulText(this.firstname())) {
      errors.push('Nome Rappresentante');
    }
    if (this.isMissingMeaningfulText(this.lastname())) {
      errors.push('Cognome Rappresentante');
    }
    if (this.isMissingMeaningfulText(this.legalRepresentativeFiscalCode())) {
      errors.push('Codice Fiscale Rappresentante');
    }
    if (!this.isAxaDeutscheBankBodyshop()) {
      if (this.isMissingMeaningfulText(this.birthMunicipality())) {
        errors.push('Comune di Nascita');
      }
      if (!this.dateOfBirth()) {
        errors.push('Data di Nascita');
      }
    }

    return errors;
  }

  /** Valida un campo telefonico: obbligatorieta', sole cifre e lunghezza 6-15 (prefisso escluso). */
  private validatePhoneField(numberValue: string, label: string, required: boolean): string[] {
    const number = (numberValue ?? '').replace(/\s+/g, '');

    if (!number) {
      return required ? [label] : [];
    }
    if (!isPhoneNumberDigitsOnly(number)) {
      return [`${label} (il campo deve contenere soltanto numeri)`];
    }
    if (!isValidNationalPhoneNumber(number)) {
      return [`${label} (formato non valido)`];
    }
    return [];
  }

  /**
   * Risolve prefisso e numero da un record di contatto.
   * Supporta sia il nuovo formato con prefisso su campo separato, sia il legacy
   * con prefisso e numero concatenati in un'unica stringa (prefisso mancante).
   */
  private resolvePhoneParts(
    rawNumber: string | null | undefined,
    rawPrefix: string | null | undefined,
  ): { prefix: string; number: string } {
    const prefix = normalizeTrimmedText(rawPrefix ?? '');
    if (prefix) {
      return { prefix, number: (rawNumber ?? '').replace(/\s+/g, '') };
    }
    const legacy = splitPhoneNumber(
      rawNumber ?? '',
      this.formDropdownService.getPhonePrefixCodesSync(),
    );
    return { prefix: legacy.prefix || DEFAULT_PHONE_PREFIX, number: legacy.number };
  }

  /**
   * Validates email format
   */
  // Email validation helper (public for template access)
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(normalizeTrimmedText(email));
  }

  isValidPhoneNumber(phone: string): boolean {
    return isValidFullPhoneNumber(phone, this.formDropdownService.getPhonePrefixCodesSync());
  }

  private getFormData() {
    // Format date to ISO string (YYYY-MM-DD) if exists
    const formatDateToISO = (date: Date | null): string => {
      if (!date) return '';
      return date.toISOString().split('T')[0];
    };

    const isAxaDeutscheBankBodyshop = this.isAxaDeutscheBankBodyshop();

    const rawData = {
      companyData: {
        firstname: this.preRegFirstName(),
        lastname: this.preRegLastName(),
        businessName: this.businessName(),
        vatNumber: this.vatNumber(),
        legalOrPhysical: this.legalOrPhysical(),
        companyType: this.companyType(),
        fiscalCode: this.fiscalCode(),
      },
      legalAddress: {
        region: this.region(),
        province: this.province(),
        zipCode: this.zipCode(),
        municipality: this.municipality(),
        streetType: this.streetType(),
        streetName: this.streetName(),
        streetNumber: this.streetNumber(),
      },
      contactInfo: {
        email: this.email(),
        institutionalEmailSameAsRegistrationEmail: this.institutionalEmailSameAsRegistrationEmail(),
        certifiedEmail: this.certifiedEmail(),
        mobilePhone: this.mobilePhone() || null,
        mobilePhonePrefix: this.mobilePhone() ? this.mobilePhonePrefix() : null,
        telephone: this.telephone() || null,
        telephonePrefix: this.telephone() ? this.telephonePrefix() : null,
        website: this.website(),
        responsibleEmail: this.responsibleEmail(),
        responsibleEmailSameAsRegistrationEmail: this.responsibleEmailSameAsRegistrationEmail(),
        mobilePhoneResponsible: this.mobilePhoneResponsible() || null,
        mobilePhoneResponsiblePrefix: this.mobilePhoneResponsible() ? this.mobilePhoneResponsiblePrefix() : null,
        ownerTelephone: this.ownerTelephone() || null,
        ownerTelephonePrefix: this.ownerTelephone() ? this.ownerTelephonePrefix() : null,
        claimsNetworkPhone: this.claimsNetworkPhone() || null,
        claimsNetworkPhonePrefix: this.claimsNetworkPhone() ? this.claimsNetworkPhonePrefix() : null,
      },
      bankDetails: {
        invoiceAddressSameAsLegalAddress: isAxaDeutscheBankBodyshop
          ? true
          : this.invoiceAddressSameAsLegalAddress(),
        invoiceParty: isAxaDeutscheBankBodyshop ? null : this.invoiceParty(),
        vatNumber: isAxaDeutscheBankBodyshop ? null : this.invoiceVatNumber(),
        fiscalRegime: this.fiscalRegime(),
        iban: normalizeIban(this.iban()),
        bankInstitutionName: this.bankInstitutionName(),
        bankInstitutionBranch: this.bankInstitutionBranch(),
        accountOwner: this.accountOwner(),
        swift: isAxaDeutscheBankBodyshop ? null : this.swift(),
        address: {
          region: isAxaDeutscheBankBodyshop ? null : this.invoiceRegion(),
          province: isAxaDeutscheBankBodyshop ? null : this.invoiceProvince(),
          zipCode: isAxaDeutscheBankBodyshop ? null : this.invoiceZipCode(),
          municipality: isAxaDeutscheBankBodyshop ? null : this.invoiceMunicipality(),
          streetType: isAxaDeutscheBankBodyshop ? null : this.invoiceStreetType(),
          streetName: isAxaDeutscheBankBodyshop ? null : this.invoiceAddress(),
          streetNumber: isAxaDeutscheBankBodyshop ? null : this.invoiceStreetNumber(),
        },
      },
      legalRepresentative: {
        firstname: this.firstname(),
        lastname: this.lastname(),
        birthMunicipality: isAxaDeutscheBankBodyshop ? null : this.birthMunicipality(),
        dateOfBirth: isAxaDeutscheBankBodyshop ? null : formatDateToISO(this.dateOfBirth()),
        fiscalCode: this.legalRepresentativeFiscalCode(),
      },
    };

    // Convert all empty strings to null before returning
    return convertEmptyStringsToNull(rawData);
  }

  private loadPersonTypeOptions(): void {
    this.formDropdownService
      .getFormDropdownValues('PERSON_TYPE', 'PERSON_TYPE')
      .pipe(catchError((err) => {
        console.error('Errore caricamento tipo persona:', err);
        this.messageService.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Impossibile caricare le opzioni Persona Fisica/Giuridica',
          life: 4000,
        });
        return of({ status: false, message: '', data: [] });
      }))
      .subscribe((response) => {
        this.personTypeOptions.set(
          (response.data || []).map((item) => ({
            label: item.valueDescription,
            value: item.valueKey,
          })),
        );
      });
  }

  private loadStreetTypeOptions(): void {
    this.formDropdownService
      .getFormDropdownValues('PLACE_NAME', 'PLACE_NAME')
      .pipe(catchError((err) => {
        console.error('Errore caricamento tipo via:', err);
        this.messageService.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Impossibile caricare le opzioni Tipo Via',
          life: 4000,
        });
        return of({ status: false, message: '', data: [] });
      }))
      .subscribe((response) => {
        this.streetTypeOptions.set(
          (response.data || []).map((item) => ({
            label: item.valueDescription,
            value: item.valueKey,
          })),
        );
      });
  }

  // Computed signal to filter supplier types based on person type
filteredSupplierTypes = computed<DropdownOption[]>(() => {
  const personType = this.legalOrPhysical();
  const allTypes = this.allSupplierTypes();
  
  if (!personType) return [];
  
  if (personType === 'FISICA') {
    // For physical persons, only "Ditta Individuale (D.I.)" is allowed
    return allTypes.filter(item => item.value === 'DI');
  } else if (personType === 'GIURIDICA') {
    // For legal persons, all types EXCEPT "Ditta Individuale (D.I.)" are allowed
    return allTypes.filter(item => item.value !== 'DI');
  }
  
  return allTypes;
});

  // Handlers for cascading geographic filters
  onRegionChange(regionCode: string | null): void {
    // Reset dependent fields
    this.province.set('');
    this.municipality.set('');
    this.provinceOptions.set([]);
    this.municipalityOptions.set([]);

    // Load provinces if region is selected
    if (regionCode) {
      this.geographyService.getProvinces(regionCode)
        .pipe(catchError((err) => {
          console.error('Errore caricamento province:', err);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare le province',
            life: 4000,
          });
          return of([]);
        }))
        .subscribe((provinces) => {
          this.provinceOptions.set(
            (provinces || []).map((item) => ({
              label: item.name,
              value: item.code,
            })),
          );
        });
    }
  }

  onProvinceChange(provinceCode: string | null): void {
    // Reset dependent fields
    this.municipality.set('');
    this.municipalityOptions.set([]);

    // Load municipalities if province is selected
    if (provinceCode) {
      this.geographyService.getMunicipalities(provinceCode)
        .pipe(catchError((err) => {
          console.error('Errore caricamento comuni:', err);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare i comuni',
            life: 4000,
          });
          return of([]);
        }))
        .subscribe((municipalities) => {
          this.municipalityOptions.set(
            (municipalities || []).map((item) => ({
              label: item.name,
              value: item.code,
            })),
          );
        });
    }
  }

  // Helper methods to check if fields should be disabled
  isProvinceDisabled(): boolean {
    return !this.region();
  }

  isMunicipalityDisabled(): boolean {
    return !this.province() || this.isProvinceDisabled();
  }

  onLegalAddressChange(): void {
    if (this.invoiceAddressSameAsLegalAddress()) {
      this.copyLegalAddressToInvoice();
    }
  }

  onInvoiceAddressSameAsLegalAddressChange(sameAddress: boolean): void {
    this.invoiceAddressSameAsLegalAddress.set(sameAddress === true);

    if (this.invoiceAddressSameAsLegalAddress()) {
      this.copyLegalAddressToInvoice();
    } else {
      this.clearInvoiceAddress();
    }
  }

  onVatNumberChange(vatNumber: string): void {
    const previousReference = this.vatNumber();
    this.vatNumber.set(vatNumber);

    if (this.isMissingMeaningfulText(vatNumber)) {
      // Company VAT cleared: hide the question, treat as different, keep written value
      this.invoiceVatNumberSameAsVatNumber.set(false);
      return;
    }

    if (this.isMissingMeaningfulText(previousReference)) {
      // Company VAT set for the first time: default to "coincide" and copy it
      this.invoiceVatNumberSameAsVatNumber.set(true);
      this.invoiceVatNumber.set(vatNumber);
      return;
    }

    // Company VAT edited while already present: propagate only when "coincide"
    if (this.invoiceVatNumberSameAsVatNumber()) {
      this.invoiceVatNumber.set(vatNumber);
    }
  }

  onInvoiceVatNumberChange(invoiceVatNumber: string): void {
    this.invoiceVatNumber.set(invoiceVatNumber);

    if (this.isMissingMeaningfulText(this.vatNumber())) {
      this.invoiceVatNumberSameAsVatNumber.set(false);
    }
  }

  onInvoiceVatNumberSameAsVatNumberChange(same: boolean): void {
    this.invoiceVatNumberSameAsVatNumber.set(same === true);

    if (this.invoiceVatNumberSameAsVatNumber()) {
      this.invoiceVatNumber.set(this.vatNumber());
    } else {
      this.invoiceVatNumber.set('');
    }
  }

  onInstitutionalEmailSameAsRegistrationEmailChange(same: boolean): void {
    this.institutionalEmailSameAsRegistrationEmail.set(same === true);

    if (this.institutionalEmailSameAsRegistrationEmail()) {
      this.email.set(this.preRegEmail());
    } else {
      this.email.set('');
    }
  }

  onResponsibleEmailSameAsRegistrationEmailChange(same: boolean): void {
    this.responsibleEmailSameAsRegistrationEmail.set(same === true);

    if (this.responsibleEmailSameAsRegistrationEmail()) {
      this.responsibleEmail.set(this.preRegEmail());
    } else {
      this.responsibleEmail.set('');
    }
  }

  onInvoiceAddressFieldChange(): void {
    if (this.invoiceAddressSameAsLegalAddress()) {
      this.copyLegalAddressToInvoice();
    }
  }

  /**
   * Sincronizza sull'indirizzo di fatturazione i soli VALORI dell'indirizzo legale,
   * senza ricaricare le liste geografiche. Usato dai campi che non impattano
   * regione/provincia/comune (via, civico, CAP, comune) quando "stesso indirizzo"
   * e' attivo: evita fetch inutili di province/comuni ad ogni modifica.
   */
  onLegalAddressValueChange(): void {
    if (this.invoiceAddressSameAsLegalAddress()) {
      this.copyLegalAddressValuesToInvoice();
    }
  }

  private copyLegalAddressValuesToInvoice(): void {
    this.invoiceStreetType.set(this.streetType());
    this.invoiceAddress.set(this.streetName());
    this.invoiceStreetNumber.set(this.streetNumber());
    this.invoiceZipCode.set(this.zipCode());
    this.invoiceMunicipality.set(this.municipality());
    this.invoiceProvince.set(this.province());
    this.invoiceRegion.set(this.region());
  }

  private copyLegalAddressToInvoice(): void {
    this.copyLegalAddressValuesToInvoice();

    this.invoiceProvinceOptions.set([]);
    this.invoiceMunicipalityOptions.set([]);

    if (this.invoiceRegion()) {
      this.geographyService.getProvinces(this.invoiceRegion())
        .pipe(catchError(() => of([])))
        .subscribe((provinces) => {
          this.invoiceProvinceOptions.set(
            (provinces || []).map((item) => ({ label: item.name, value: item.code })),
          );
        });
    }

    if (this.invoiceProvince()) {
      this.geographyService.getMunicipalities(this.invoiceProvince())
        .pipe(catchError(() => of([])))
        .subscribe((municipalities) => {
          this.invoiceMunicipalityOptions.set(
            (municipalities || []).map((item) => ({ label: item.name, value: item.code })),
          );
        });
    }
  }

  private clearInvoiceAddress(): void {
    this.invoiceStreetType.set('');
    this.invoiceAddress.set('');
    this.invoiceStreetNumber.set(null);
    this.invoiceZipCode.set('');
    this.invoiceMunicipality.set('');
    this.invoiceProvince.set('');
    this.invoiceRegion.set('');
    this.invoiceProvinceOptions.set([]);
    this.invoiceMunicipalityOptions.set([]);
  }

  // Billing address cascading filters
  onInvoiceRegionChange(regionCode: string | null): void {
    // Reset dependent fields
    this.invoiceProvince.set('');
    this.invoiceMunicipality.set('');
    this.invoiceProvinceOptions.set([]);
    this.invoiceMunicipalityOptions.set([]);

    // Load provinces if region is selected
    if (regionCode) {
      this.geographyService.getProvinces(regionCode)
        .pipe(catchError((err) => {
          console.error('Errore caricamento province fatturazione:', err);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare le province',
            life: 4000,
          });
          return of([]);
        }))
        .subscribe((provinces) => {
          this.invoiceProvinceOptions.set(
            (provinces || []).map((item) => ({
              label: item.name,
              value: item.code,
            })),
          );
        });
    }
  }

  onInvoiceProvinceChange(provinceCode: string | null): void {
    // Reset dependent fields
    this.invoiceMunicipality.set('');
    this.invoiceMunicipalityOptions.set([]);

    // Load municipalities if province is selected
    if (provinceCode) {
      this.geographyService.getMunicipalities(provinceCode)
        .pipe(catchError((err) => {
          console.error('Errore caricamento comuni fatturazione:', err);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare i comuni',
            life: 4000,
          });
          return of([]);
        }))
        .subscribe((municipalities) => {
          this.invoiceMunicipalityOptions.set(
            (municipalities || []).map((item) => ({
              label: item.name,
              value: item.code,
            })),
          );
        });
    }
  }

  isInvoiceProvinceDisabled(): boolean {
    return !this.invoiceRegion();
  }

  isInvoiceMunicipalityDisabled(): boolean {
    return !this.invoiceProvince() || this.isInvoiceProvinceDisabled();
  }

  // Handler for person type change
onPersonTypeChange(newValue: string | null): void {
  const currentCompanyType = this.companyType();
  
  // Reset company type when person type changes
  if (newValue === 'FISICA') {
    // If switching to Physical Person and current type is not DI, reset
    if (currentCompanyType !== 'DI') {
      this.companyType.set('');
    }
  } else if (newValue === 'GIURIDICA') {
    // If switching to Legal Person and current type is DI, reset
    if (currentCompanyType === 'DI') {
      this.companyType.set('');
    }
  } else {
    // If person type is cleared, reset company type
    this.companyType.set('');
  }
  
  this.companyTypeError.set('');
}

  private loadCompanyTypeOptions(): void {
    this.formDropdownService
      .getFormDropdownValues('LEGAL_PERSON', 'LEGAL_PERSON')
      .pipe(catchError((err) => {
        console.error('Errore caricamento tipo società :', err);
        this.messageService.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Impossibile caricare le opzioni Tipo Società ',
          life: 4000,
        });
        return of({ status: false, message: '', data: [] });
      }))
      .subscribe((response) => {
        const options = (response.data || []).map((item) => ({
          label: item.valueDescription,
          value: item.valueKey,
          disabled: item.disabled,
        }));
        this.allSupplierTypes.set(options);
        this.companyTypeOptions.set(options); // Keep for backward compatibility

        if (
          this.invoicePartyAutoFillValue !== null &&
          this.invoiceParty() === this.invoicePartyAutoFillValue
        ) {
          const defaultInvoiceParty = this.getDefaultInvoiceParty();
          this.invoiceParty.set(defaultInvoiceParty);
          this.invoicePartyAutoFillValue = defaultInvoiceParty;
        }
      });
  }

  private getDefaultInvoiceParty(): string {
    return this.getExpectedBusinessNameWithCompanyType();
  }

  private loadRegionsOptions(): void {
    this.geographyService
      .getRegions()
      .pipe(catchError((err) => {
        console.error('Errore caricamento regioni:', err);
        this.messageService.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Impossibile caricare le regioni',
          life: 4000,
        });
        return of([]);
      }))
      .subscribe((regions) => {
        this.regionOptions.set(
          (regions || []).map((item) => ({
            label: item.name,
            value: item.code,
          })),
        );
      });
  }

  private loadInvoiceRegionsOptions(): void {
    this.geographyService
      .getRegions()
      .pipe(catchError((err) => {
        console.error('Errore caricamento regioni fatturazione:', err);
        this.messageService.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Impossibile caricare le regioni',
          life: 4000,
        });
        return of([]);
      }))
      .subscribe((regions) => {
        this.invoiceRegionOptions.set(
          (regions || []).map((item) => ({
            label: item.name,
            value: item.code,
          })),
        );
      });
  }

  private loadTaxRegimeOptions(): void {
    this.formDropdownService
      .getFormDropdownValues('TAX_REGIME_APPLIED', 'TAX_REGIME_APPLIED')
      .pipe(catchError((err) => {
        console.error('Errore caricamento regime fiscale:', err);
        this.messageService.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Impossibile caricare le opzioni Regime Fiscale',
          life: 4000,
        });
        return of({ status: false, message: '', data: [] });
      }))
      .subscribe((response) => {
        this.taxRegimeOptions.set(
          (response.data || []).map((item) => ({
            label: item.valueDescription,
            value: item.valueKey,
          })),
        );
      });
  }
}


